import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import supplierService from '../../services/supplierService';
import { IcArrowLeft, IcBuilding, IcMail, IcPhone, IcMapPin, IcContact, IcLandmark, IcSave, IcPlus, IcTrash, IcTruck } from '../../theme/icons';
import { BtnSk } from '../../components/Skeleton';

// Shared input styling (matches the modern look used across the app, e.g. AddCustomer)
// Formulaire (Brand Kit §05) : champ .inp 42px bordé #D1D5DB rayon 6,
// 13.5px, padding 11, focus 2px encre ; libellé 12px 500 encre, mb 4.
const inputClass = 'inp w-full px-3.5 py-2.5 rounded-xl text-base text-gray-900';
const labelClass = 'block text-sm font-medium text-gray-900 mb-1.5';

const AddSupplier = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    address: '',
    registre_commercial: '',
    matricule_fiscale: '',
    rib: '',
    bank_name: '',
  });
  // Multiple phone numbers — managed as a list, stored comma-separated on submit.
  const [phones, setPhones] = useState(['']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handlePhoneChange = (index, value) => {
    setPhones((prev) => prev.map((p, i) => (i === index ? value : p)));
  };

  const addPhone = () => setPhones((prev) => [...prev, '']);

  const removePhone = (index) => {
    setPhones((prev) => (prev.length === 1 ? [''] : prev.filter((_, i) => i !== index)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Le nom du fournisseur est requis.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      // Join non-empty phone numbers into a single comma-separated string.
      const phone = phones.map((p) => p.trim()).filter(Boolean).join(', ');
      await supplierService.createSupplier({ ...formData, phone });
      navigate('/suppliers');
    } catch (err) {
      console.error('Error creating supplier:', err);
      let message = err.message;
      if (!message && err && typeof err === 'object') {
        const first = Object.values(err)[0];
        message = Array.isArray(first) ? first[0] : first;
      }
      setError(message || 'Une erreur est survenue lors de la création du fournisseur.');
      setLoading(false);
    }
  };

  return (
    <div className="w-full px-4 pt-2 pb-4 max-w-5xl">
      {/* Back link */}
      <Link to="/suppliers" className="inline-flex items-center px-5 py-2.5 text-sm mb-4 btn-s transition-colors">
        <IcArrowLeft className="mr-2" /> Retour
      </Link>

      {/* Page name as a gray chip — same design as the AddProduct page
          (owner Studio); old gradient icon box + h1 + subtitle removed. */}
      <div className="mb-6">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border bg-gray-100 border-gray-400 text-gray-500 shadow-sm">
          <IcTruck size={13} /> Ajouter un nouveau fournisseur
        </span>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* ===== Informations générales ===== */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-sm font-bold text-gray-700 mb-5 flex items-center gap-2">
            <span className="h-7 w-7 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center"><IcBuilding className="text-xs" /></span>
            Informations générales
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className={labelClass} htmlFor="name">Nom <span className="req">*</span></label>
              <input type="text" id="name" name="name" value={formData.name} onChange={handleChange}
                className={inputClass} placeholder="Nom du fournisseur" required />
            </div>
            <div>
              <label className={labelClass} htmlFor="email">Email</label>
              <div className="relative">
                <IcMail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange}
                  className={`${inputClass} pl-10`} placeholder="Email du fournisseur" />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className={labelClass} htmlFor="address">Adresse</label>
              <div className="relative">
                <IcMapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input type="text" id="address" name="address" value={formData.address} onChange={handleChange}
                  className={`${inputClass} pl-10`} placeholder="Adresse du fournisseur" />
              </div>
            </div>
          </div>
        </div>

        {/* ===== Téléphones ===== */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-sm font-bold text-gray-700 flex items-center gap-2">
              <span className="h-7 w-7 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center"><IcPhone className="text-xs" /></span>
              Numéros de téléphone
            </h2>
            <button type="button" onClick={addPhone}
              className="flex items-center gap-1.5 text-sm btn-add-link">
              <IcPlus className="text-xs" /> Ajouter un numéro
            </button>
          </div>
          <div className="space-y-3">
            {phones.map((phone, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="relative flex-1">
                  <IcPhone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => handlePhoneChange(index, e.target.value)}
                    className={`${inputClass} pl-10`}
                    placeholder={index === 0 ? 'Numéro principal' : `Numéro ${index + 1}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removePhone(index)}
                  disabled={phones.length === 1 && !phones[0]}
                  className={`h-10 w-10 shrink-0 flex items-center justify-center rounded-xl transition-colors ${
                    phones.length === 1 && !phones[0]
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-red-600 hover:bg-red-50'
                  }`}
                  title="Supprimer ce numéro"
                >
                  <IcTrash className="text-sm" />
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3">Vous pouvez enregistrer plusieurs numéros pour ce fournisseur.</p>
        </div>

        {/* ===== Informations légales ===== */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-sm font-bold text-gray-700 mb-5 flex items-center gap-2">
            <span className="h-7 w-7 rounded-lg bg-sky-100 text-sky-500 flex items-center justify-center"><IcContact className="text-xs" /></span>
            Informations légales
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass} htmlFor="registre_commercial">Registre Commercial</label>
              <input type="text" id="registre_commercial" name="registre_commercial" value={formData.registre_commercial}
                onChange={handleChange} className={inputClass} placeholder="Registre commercial" />
            </div>
            <div>
              <label className={labelClass} htmlFor="matricule_fiscale">Matricule Fiscale</label>
              <input type="text" id="matricule_fiscale" name="matricule_fiscale" value={formData.matricule_fiscale}
                onChange={handleChange} className={inputClass} placeholder="Matricule fiscale" />
            </div>
          </div>
        </div>

        {/* ===== Informations bancaires ===== */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-sm font-bold text-gray-700 mb-5 flex items-center gap-2">
            <span className="h-7 w-7 rounded-lg bg-green-100 text-green-600 flex items-center justify-center"><IcLandmark className="text-xs" /></span>
            Informations bancaires
          </h2>
          <div className="grid grid-cols-1 gap-5">
            <div>
              <label className={labelClass} htmlFor="rib">RIB</label>
              <input type="text" id="rib" name="rib" value={formData.rib} onChange={handleChange}
                className={`${inputClass} font-mono tracking-wide`} placeholder="RIB" />
            </div>
            <div>
              <label className={`${labelClass} flex items-center gap-2`} htmlFor="bank_name">
                <IcLandmark className="text-green-600" /> Nom de la banque
              </label>
              <input type="text" id="bank_name" name="bank_name" value={formData.bank_name} onChange={handleChange}
                className={inputClass} placeholder="Nom de la banque" />
            </div>
          </div>
        </div>

        {/* Footer actions — pied de formulaire (Brand Kit §05) : filet gray-100, pt 12, gap 8 */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
          <Link to="/suppliers" className="px-5 py-2.5 btn-g transition-colors">
            Annuler
          </Link>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center py-2.5 px-6 btn-p transition-colors"
          >
            {loading ? (
              <>
                <BtnSk />
                Enregistrement...
              </>
            ) : (
              <>
                <IcSave className="mr-2" /> Enregistrer
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddSupplier;
